import type { BookingBlDocument, BookingDocument } from "@/types/api";

import { isBlDocumentDisplayable } from "./bookingBlDocuments";

/** Documents that arrived on the same Graph mail message. */
export interface BookingDocumentGroup {
  message_id: string;
  mailbox_id: string;
  received_at: string | null;
  subject: string | null;
  category: string | null;
  from_address: string | null;
  documents: BookingDocument[];
}

/** Keep the newest attachment per filename (case-insensitive). */
export function deduplicateBookingDocumentsByFilename(
  documents: BookingDocument[],
): BookingDocument[] {
  const latestByFilename = new Map<string, BookingDocument>();
  for (const doc of documents) {
    const key = doc.filename.toLowerCase();
    const existing = latestByFilename.get(key);
    if (!existing) {
      latestByFilename.set(key, doc);
      continue;
    }
    const docTime = doc.received_at ? Date.parse(doc.received_at) : 0;
    const existingTime = existing.received_at
      ? Date.parse(existing.received_at)
      : 0;
    if (docTime >= existingTime) {
      latestByFilename.set(key, doc);
    }
  }
  return [...latestByFilename.values()];
}

/** Group linked documents by parent mail, newest `received_at` first. */
export function groupBookingDocumentsByReceived(
  documents: BookingDocument[],
): BookingDocumentGroup[] {
  const groups = new Map<string, BookingDocumentGroup>();
  for (const doc of documents) {
    const existing = groups.get(doc.message_id);
    if (existing) {
      existing.documents.push(doc);
      continue;
    }
    groups.set(doc.message_id, {
      message_id: doc.message_id,
      mailbox_id: doc.mailbox_id,
      received_at: doc.received_at,
      subject: doc.subject,
      category: doc.category,
      from_address: doc.from_address,
      documents: [doc],
    });
  }
  return [...groups.values()].sort((a, b) => {
    const at = a.received_at ? Date.parse(a.received_at) : 0;
    const bt = b.received_at ? Date.parse(b.received_at) : 0;
    return bt - at;
  });
}

/** Match a mail/invoice document to its parsed bill-of-lading details. */
export function findLinkedBlDocument(
  document: BookingDocument,
  blDocuments: BookingBlDocument[],
): BookingBlDocument | undefined {
  const byAttachment = blDocuments.find(
    (bl) => bl.attachment_id === document.attachment_id,
  );
  if (byAttachment && isBlDocumentDisplayable(byAttachment)) {
    return byAttachment;
  }
  const name = document.filename.toLowerCase();
  return blDocuments.find(
    (bl) =>
      isBlDocumentDisplayable(bl) && bl.filename.toLowerCase() === name,
  );
}

export function formatDocumentBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Customer-facing badge when an extracted document drove a booking update. */
export const BOOKING_UPDATE_DOCUMENT_BADGE = {
  label: "Booking update",
  title: "This document was extracted and used to update the booking",
} as const;

/** True when doc-intel extracted this file onto the booking-update partition. */
export function isUsedForBookingUpdates(document: BookingDocument): boolean {
  return document.used_for_booking_updates === true;
}
